import { getArticleDetail } from '@libs/catalog-service';
import { QUERY_KEYS } from '@libs/constants';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { mapArticleDetailResponseToPrices } from '@libs/product-price-utils';
import { useSessionStore } from '@hooks/states/useSessionStore';
import type { AssociatedProductPrice, SharedProductPagePropsPrices } from '@product/ProductPage/ProductPage';
import { getProductIdForLocale } from '@libs/product.helper';
import { usePriceCountry } from '@category/CategoryPage/hooks/usePriceCountry';
import { useProductPageStore } from './states/useProductPageStore';
import { useHasDynamicPrice } from './useHasDynamicPrice';
import type { PriceData } from './states/pricesSlice';
import { initializePriceData } from './states/pricesSlice';
import { GraphQlPriceType } from '@models/graphql';
import type { ArticleDetailRequest } from '@models/api/shopping-cart.types';

const LOAD_DYNAMIC_PRICES_FOR_PRICE_TYPES = [GraphQlPriceType.LeadScrewLength];

export const usePriceOverwrites = (
  associatedProductIds: string[],
  locale: string,
  onError: () => void
): SharedProductPagePropsPrices => {
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const priceData = useProductPageStore(state => state.priceData);
  const hasDynamicPrices = useHasDynamicPrice();
  const loadDynamicPriceForPriceType = LOAD_DYNAMIC_PRICES_FOR_PRICE_TYPES.includes(priceData.priceType);
  const session = useSessionStore(state => state.session);

  const [updatedPriceData, setUpdatedPriceData] = useState<PriceData>(initializePriceData(currentProduct));

  const [associatedProductPrices, setAssociatedProductPrices] = useState<AssociatedProductPrice[]>([]);

  const priceCountry = usePriceCountry();
  const currentProductIdForPriceRequest = getProductIdForLocale(currentProduct, locale);

  const length = useProductPageStore(state => state.length);
  const unitLabel = useProductPageStore(state => state.unitLabel);

  const articleDetailParams: ArticleDetailRequest = {
    articleNumbers: [currentProductIdForPriceRequest, ...associatedProductIds],
    configurationType: 'ISQ',
  };

  if (loadDynamicPriceForPriceType) {
    articleDetailParams.length = length;
    articleDetailParams.unit = unitLabel;
    articleDetailParams.priceCountry = priceCountry;
  }

  const { data: articleDetailsResponse, status: fetchingDiscountPricesStatus } = useQuery(
    [
      QUERY_KEYS.ARTICLE_DETAIL_NUMBER_MODE,
      currentProduct.id,
      hasDynamicPrices,
      loadDynamicPriceForPriceType,
      ...associatedProductIds,
      length,
    ],
    () => getArticleDetail({ ...articleDetailParams, textMode: false }),
    {
      enabled: (hasDynamicPrices || loadDynamicPriceForPriceType) && !!session,
      onError: () => onError(),
    }
  );

  useEffect(() => {
    if (!articleDetailsResponse) {
      setUpdatedPriceData(initializePriceData(currentProduct));
      return;
    }

    setUpdatedPriceData({ ...updatedPriceData, currencyCode: articleDetailsResponse.currency });

    const prices = mapArticleDetailResponseToPrices(
      articleDetailsResponse,
      priceData.priceType,
      articleDetailsResponse.currency
    );

    const currentProductPrices = prices.find(p => p.id === currentProductIdForPriceRequest);
    if (currentProductPrices) {
      setUpdatedPriceData({
        ...updatedPriceData,
        currencyCode: articleDetailsResponse.currency,
        discountQuantity: currentProductPrices.quantityDiscounts,
        basePricePerUnit: currentProductPrices.baseUnitCentPrice,
        additionalLengthPrice: currentProductPrices.lengthUnitCentPrice ?? null,
      });
    }

    setAssociatedProductPrices(prices.filter(p => p.id !== currentProductIdForPriceRequest));
  }, [articleDetailsResponse, currentProductIdForPriceRequest, priceData.priceType, currentProduct]);

  return {
    fetchingDiscountPricesStatus,
    unitBasePrice: updatedPriceData.basePricePerUnit,
    unitLengthPrice: updatedPriceData.additionalLengthPrice,
    discountQuantityPrices: updatedPriceData.discountQuantity,
    associatedProductPrices,
    currencyCode: updatedPriceData.currencyCode,
  };
};
